import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { I18nextProvider } from 'react-i18next';
import i18n from './config/i18n';
import { LoadingProvider } from './context/LoadingContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AIAssistantPage from './pages/AIAssistantPage';
import NotFound from './pages/NotFound';

// Components
import LoadingScreen from './components/LoadingScreen';

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen message="Authenticating..." />;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <Router>
        <AuthProvider>
          <ThemeProvider>
            <LoadingProvider>
              <CssBaseline />
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                
                {/* Protected Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/ai-assistant" 
                  element={
                    <ProtectedRoute>
                      <AIAssistantPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Redirect root to dashboard or login */}
                <Route 
                  path="/" 
                  element={
                    <AuthWrapper>
                      {(currentUser) => 
                        currentUser ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
                      }
                    </AuthWrapper>
                  } 
                />
                
                {/* 404 Page */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LoadingProvider>
          </ThemeProvider>
        </AuthProvider>
      </Router>
    </I18nextProvider>
  );
};

// Helper component to access auth context in route
interface AuthWrapperProps {
  children: (currentUser: any) => React.ReactNode;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen message="Preparing your tax assistant..." />;
  }
  
  return <>{children(currentUser)}</>;
};

export default App;
